import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  ListGroup,
  Button,
  Card,
  Accordion,
  Form,
} from 'react-bootstrap';
import {
  FaBox,
  FaShoppingCart,
  FaMoneyBill,
  FaCashRegister,
  FaUsers,
  FaStickyNote,
  FaTrash,
} from 'react-icons/fa';
import Message from '../components/Message';
import { addToCart, removeFromCart } from '../slices/cartSlice';

const AdminForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { cartItems } = useSelector((state) => state.cart);

  const addToCartHandler = (product, qty) => {
    dispatch(addToCart({ ...product, qty }));
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
  };

  // ⭐ NAVIGATION TARGETS GROUPED BY CATEGORY
  const categories = {
    'Products & Stock': {
      color: '#007bff',
      icon: <FaBox className="me-2" />,
      items: {
        'admin/productlist': 'ProductList',
        'admin/stocklist': 'Stocks',
        'admin/openingstocklist': 'OpeningStock',
        'admin/stockbulkeditscreen': 'Stock Bulk Update',
        stockreport: 'Credit Sales',
        consolidatedbalance: 'Consolidated-Stock-Analysis',
        consolidatedworkingcapital: 'Consolidated-Working-Capital',

      },
    },

    'Sales & Purchases': {
      color: '#28a745',
      icon: <FaShoppingCart className="me-2" />,
      items: {
        'admin/salelist': 'Sales',
        'admin/supplylist': 'Purchases',
        'admin/transferlist': 'Transfers',
        'admin/supplysummary': 'General Supply Summary',
      },
    },

    Expenses: {
      color: '#ffc107',
      icon: <FaMoneyBill className="me-2" />,
      items: {
        'admin/expenselist': 'Income / Expense',
        'admin/expensecategory': 'Expense-Category',
        'admin/incomeexpensecategories': 'IncEx Category',
        'admin/incomecategory': 'CashCollected-Category',
        'admin/expenseinvoices': 'Gen Invoice',
        'admin/expenseinvoicesummary': 'EI Invoice Num',
        'admin/supplyinvoicesummary': 'SupplyNum',
      },
    },

    'Cash & Bank': {
      color: '#17a2b8',
      icon: <FaCashRegister className="me-2" />,
      items: {
        netcash: 'Cash Collected',
        bankuserlist: 'Bank-Staffpage',
        'admin/depositlist': 'Bank',
      },
    },

    'Customer & Suppliers': {
      color: '#6f42c1',
      icon: <FaUsers className="me-2" />,
      items: {
        'admin/customerlist': 'Customerlist',
        'admin/supplierlist': 'Supplierlist',
        'admin/supplierbalance': 'SupplierBalance',
        'admin/adminuserscreen': 'ChangeUserRole',
      },
    },

    'Notes & Misc': {
      color: '#fd7e14',
      icon: <FaStickyNote className="me-2" />,
      items: {
        'admin/baglist': 'General Notes',
        'admin/orderlist': 'OrderList',
        alltotal: 'Cashflow',
      },
    },
  };

  // ⭐ UNIVERSAL NAVIGATION HANDLER
  const navHandler = (path) => {
    navigate(`/login?redirect=/${path}`);
  };

  // ⭐ SEARCH STATE
  const [search, setSearch] = useState('');

  // ⭐ FILTERED BUTTONS
  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;

    const lower = search.toLowerCase();
    const result = {};

    Object.entries(categories).forEach(([catName, catData]) => {
      const matchedItems = Object.entries(catData.items).filter(([path, label]) =>
        label.toLowerCase().includes(lower)
      );

      if (matchedItems.length > 0) {
        result[catName] = {
          ...catData,
          items: Object.fromEntries(matchedItems),
        };
      }
    });

    return result;
  }, [search]);

  return (
    <Row>
      {/* LEFT PANEL */}
      <Col md={8}>
        <h1 className="mb-3">Browse Pages</h1>

        {cartItems.length === 0 ? (
          <Message>
            Go <Link to="/house/HomeScreen">Back</Link>
          </Message>
        ) : (
          <ListGroup variant="flush">
            {cartItems.map((item) => (
              <ListGroup.Item key={item._id}>
                <Row>
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Col>

                  <Col md={3}>
                    <Link to={`/product/${item._id}`}>{item.name}</Link>
                  </Col>

                  <Col md={2}>${item.price}</Col>

                  <Col md={2}>
                    <Button
                      type="button"
                      variant="light"
                      onClick={() =>
                        addToCartHandler(item, item.qty > 1 ? item.qty - 1 : 1)
                      }
                      disabled={item.qty === 1}
                    >
                      -
                    </Button>
                    <span className="mx-2">{item.qty}</span>
                    <Button
                      type="button"
                      variant="light"
                      onClick={() => addToCartHandler(item, item.qty + 1)}
                      disabled={item.qty === item.countInStock}
                    >
                      +
                    </Button>
                  </Col>

                  <Col md={2}>${item.price * item.qty}</Col>

                  <Col md={1}>
                    <Button
                      type="button"
                      variant="light"
                      onClick={() => removeFromCartHandler(item._id)}
                    >
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      {/* RIGHT PANEL */}
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>
                Subtotal ({cartItems.reduce((acc, item) => acc + item.qty, 0)}) items
              </h2>
              $
              {cartItems
                .reduce((acc, item) => acc + item.qty * item.price, 0)
                .toFixed(2)}
            </ListGroup.Item>

            {/* SEARCH BAR */}
            <ListGroup.Item>
              <Form.Control
                type="text"
                placeholder="Search pages..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </ListGroup.Item>

            {/* COLLAPSIBLE SECTIONS */}
            <ListGroup.Item>
              <Accordion alwaysOpen>
                {Object.entries(filteredCategories).map(
                  ([categoryName, { color, icon, items }], idx) => (
                    <Accordion.Item eventKey={idx.toString()} key={categoryName}>
                      <Accordion.Header>
                        <span style={{ color }}>{icon}{categoryName}</span>
                      </Accordion.Header>

                      <Accordion.Body>
                        <Row>
                          {Object.entries(items).map(([path, label]) => (
                            <Col md={6} className="mb-2" key={path}>
                              <Button
                                className="w-100"
                                style={{ backgroundColor: color, borderColor: color }}
                                onClick={() => navHandler(path)}
                              >
                                {label}
                              </Button>
                            </Col>
                          ))}
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  )
                )}
              </Accordion>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default AdminForm;
